import React, { FC, useEffect, useRef } from 'react';

import styled from 'styled-components';

import { useAdyenScript } from 'hooks/use-adyen-script';
import { useLocale } from 'state/intl';

import { AdyenAcceptedCards, AdyenLocales, IAdyenCustomCardInputs } from './types';

const AdyenInputsWrapper = styled.div`
  margin-bottom: 1.25rem;
  width: 100%;
  & label {
    > span {
      color: ${Styles.color.grey.one};
    }
    > div > [data-cse^='encrypted'] {
      border: 1px solid ${Styles.color.grey.four};
      min-height: 50px;
      border-radius: ${Styles.borderRadius};
    }
  }
`;

const AdyenCustomCardInputs: FC<IAdyenCustomCardInputs> = ({ onChange, onError }) => {
  // Launches Adyen script/styles to render credit card inputs
  const { loaded } = useAdyenScript();
  const adyenInputs = useRef(null);
  const { language } = useLocale();
  const currentAdyenLocale = AdyenLocales[language];

  // Creates an instance of Adyen Checkout and renders credit card inputs when Adyen script is loaded
  useEffect(() => {
    if (loaded && window.AdyenCheckout) {
      const configuration = {
        locale: currentAdyenLocale,
        environment: 'test',
        // TO DO: This will be updated to include a clientKey for live updates
        clientKey: 'test_DSOMCBFGFFH53KRS7YKVXYMWOUIP4ANG'
      };
      const checkout = new window.AdyenCheckout(configuration);
      checkout
        .create('card', {
          // accepted card types
          brands: [
            AdyenAcceptedCards.AMEX,
            AdyenAcceptedCards.MASTERCARD,
            AdyenAcceptedCards.VISA,
            AdyenAcceptedCards.DISCOVER
          ],
          // TO DO: Customize styles to match current inputs
          styles: {
            base: {
              color: Styles.color.black,
              fontSize: '0.9375rem',
              fontFamily: Styles.fontFamily.body,
              fontWeight: Styles.fontWeight.light,
              padding: '0 .5rem'
            }
          },
          /** TO DO: Match placeholders/labels to existing fields
           * Optional. Define custom placeholders for the Card fields
           * https://docs.adyen.com/developers/checkout/api-integration/configure-secured-fields/styling-secured-fields
           */
          placeholders: {},
          /** TO DO: Implement with GQL changes
           * called when user provides the required payment details
           */
          onChange,
          onError
        })
        .mount(adyenInputs.current);
    }
  }, [loaded, currentAdyenLocale, onChange, onError]);

  return <AdyenInputsWrapper ref={adyenInputs} data-testid="adyen-credit-card-fields" />;
};

export default AdyenCustomCardInputs;
